import { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Users, UserX, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { userService } from '../../services/userService';
import { UI, buttonStyle, ui } from '../../utils/ui';

const rolePill = (role) => {
  const map = {
    customer: { backgroundColor: '#dbeafe', color: '#1e40af', borderColor: '#93c5fd' },
    agent: { backgroundColor: '#ede9fe', color: '#5b21b6', borderColor: '#c4b5fd' },
    owner: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
  };
  const c = map[role] || map.customer;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 900,
    border: `1px solid ${c.borderColor}`,
    backgroundColor: c.backgroundColor,
    color: c.color,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  };
};

const statusPill = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  borderRadius: 9999,
  fontSize: 11,
  fontWeight: 900,
  border: `1px solid ${active ? '#86efac' : '#fecaca'}`,
  backgroundColor: active ? '#dcfce7' : '#fee2e2',
  color: active ? '#166534' : '#991b1b',
  whiteSpace: 'nowrap',
});

const spinnerStyle = (color = UI.colors.danger) => ({
  width: 12,
  height: 12,
  border: `2px solid rgba(255,255,255,0.35)`,
  borderTopColor: color,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
});

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deactivating, setDeactivating] = useState(null);

  useEffect(() => {
    userService.getAllUsers()
      .then((res) => setUsers(res.data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const doDeactivate = async (id, name) => {
    setDeactivating(id);
    try {
      await userService.deactivateUser(id);
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, is_active: false } : u)));
      toast.success(`${name}'s account has been deactivated.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate user.');
    } finally {
      setDeactivating(null);
    }
  };

  const handleDeactivate = (id, name) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: 900, color: UI.colors.text }}>Deactivate {name}'s account?</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => { toast.dismiss(t.id); doDeactivate(id, name); }}
            style={{ ...buttonStyle('danger'), padding: '8px 12px', borderRadius: 12 }}
          >
            Deactivate
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ ...buttonStyle('outline'), padding: '8px 12px', borderRadius: 12 }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => {
      const matchSearch = (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
      const matchRole = roleFilter === 'all' || u.user_type === roleFilter;
      return matchSearch && matchRole;
    });
  }, [roleFilter, search, users]);

  const counts = useMemo(() => ({
    all: users.length,
    customer: users.filter((u) => u.user_type === 'customer').length,
    agent: users.filter((u) => u.user_type === 'agent').length,
  }), [users]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const wrapStyle = { display: 'flex', flexDirection: 'column', gap: 16 };
  const titleStyle = { fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text };
  const subtitleStyle = { margin: '8px 0 0', fontSize: 14, color: UI.colors.muted };

  const searchWrapStyle = {
    flex: 1,
    minWidth: 260,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 14,
    border: `1.5px solid ${UI.colors.border}`,
    backgroundColor: '#ffffff',
  };
  const fieldCore = { width: '100%', border: 'none', outline: 'none', fontSize: 14, backgroundColor: 'transparent', color: UI.colors.text };

  const statCard = (accent, bg) => ({
    ...ui.card,
    padding: 18,
    textAlign: 'center',
    backgroundColor: bg,
    borderColor: 'rgba(0,0,0,0)',
  });

  return (
    <div style={wrapStyle}>
      {/* Header */}
      <div>
        <h1 style={titleStyle}>Manage Users</h1>
        <p style={subtitleStyle}>{users.length} registered users</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {[
          { label: 'Total Users', value: counts.all, color: UI.colors.info, bg: 'rgba(37,99,235,0.10)' },
          { label: 'Customers', value: counts.customer, color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
          { label: 'Agents', value: counts.agent, color: '#7c3aed', bg: 'rgba(124,58,237,0.10)' },
        ].map((item) => (
          <div key={item.label} style={statCard(item.color, item.bg)}>
            <p style={{ margin: 0, fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: item.color }}>{item.value}</p>
            <p style={{ margin: '6px 0 0', fontSize: 11, fontWeight: 900, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{item.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <div style={searchWrapStyle}>
          <Search size={16} color="#9ca3af" />
          <input type="text" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} style={fieldCore} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
          <Filter size={16} color="#9ca3af" />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: 14, fontWeight: 800, color: UI.colors.text, cursor: 'pointer' }}
          >
            <option value="all">All Roles</option>
            <option value="customer">Customers</option>
            <option value="agent">Agents</option>
            <option value="owner">Owners</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={ui.card}>
        <div style={{ padding: 22 }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 18px', color: UI.colors.muted }}>
              <Users size={44} color="#9ca3af" style={{ opacity: 0.3, margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontWeight: 900 }}>No users found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 860 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr', gap: 12, padding: '10px 12px', borderRadius: 14, backgroundColor: '#f3f4f6', border: `1px solid ${UI.colors.border}`, fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>
                  <span>User</span><span>Email</span><span>Role</span><span>Joined</span><span>Status</span>
                </div>
                {filtered.map((u) => {
                  const role = u.user_type || 'customer';
                  const avatar = rolePill(role);
                  const avatarBg = role === 'customer' ? '#dbeafe' : role === 'agent' ? '#ede9fe' : '#dcfce7';
                  const avatarFg = role === 'customer' ? '#1d4ed8' : role === 'agent' ? '#6d28d9' : '#166534';

                  return (
                    <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr', gap: 12, padding: '12px 12px', borderBottom: `1px solid ${UI.colors.border}`, alignItems: 'center', fontSize: 13 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9999, backgroundColor: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontWeight: 900, color: avatarFg }}>{(u.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <span style={{ fontWeight: 800, color: UI.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <Mail size={14} color="#9ca3af" />
                        <span style={{ fontSize: 12, color: UI.colors.muted, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                      </div>

                      <span style={avatar}>{role}</span>
                      <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 800 }}>{new Date(u.created_at).toLocaleDateString()}</span>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        <span style={statusPill(Boolean(u.is_active))}>{u.is_active ? 'Active' : 'Inactive'}</span>
                        {u.is_active && u.user_type !== 'owner' && (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(u.id, u.name)}
                            disabled={deactivating === u.id}
                            style={{
                              ...buttonStyle('outline'),
                              padding: '8px 10px',
                              borderRadius: 12,
                              borderColor: '#fecaca',
                              color: UI.colors.danger,
                              opacity: deactivating === u.id ? 0.75 : 1,
                            }}
                            aria-label="Deactivate user"
                          >
                            {deactivating === u.id ? <span style={spinnerStyle(UI.colors.danger)} /> : <UserX size={16} />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;

